import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils"; // Utility for conditionally joining class names
import { Button } from "@/components/ui/button";
import {  IconNumber1, IconNumber2, IconNumber3, IconNumber4, IconNumber5, IconNumber6  } from "@tabler/icons-react";
import ButtonPrime from "@/components/navigation/ButtonPrime";
import BeratungsterminModal from "@/components/form/BeratungsterminModal";
import { CalendarDateRangeIcon } from "@heroicons/react/24/outline";

const tabs = [
  {
    id: "beratung",
    label: "Beratung & Wertermittlung",
    icon: <IconNumber1 className="size-4 md:size-auto"/>,
    title: "Mit Klarheit starten",
    content:
      "Eine Immobilie erfolgreich zu verkaufen, bedeutet, schnell und gezielt den passenden Käufer zu finden. Unsere Experten bieten Ihnen eine kompetente Beratung.",
    image: "/Fotos/verkaufen-steps-beratung-jakobs.jpg",
  },
  {
    id: "bewertung",
    label: "Exposé & Vorbereitung",
    title: "Ihre Immobilie im besten Licht",
    icon: <IconNumber2  className="size-4 md:size-auto" />,
    content:
      "Eine professionelle Immobilienbewertung ist der Schlüssel zu einem erfolgreichen Verkauf. Lassen Sie Ihr Objekt von Experten analysieren.",
    image: "/Fotos/verkaufen-steps-expose-jakobs.jpg",
  },
  {
    id: "expose",
    label: "Immobilienvermarktung",
    title: "Den richtigen Käufer finden",
    icon: <IconNumber3  className="size-4 md:size-auto" />,
    content:
      "Ein hochwertiges Exposé präsentiert Ihre Immobilie im besten Licht und erhöht die Chancen auf eine schnelle Vermittlung.",
    image: "/Fotos/verkaufen-steps-vermarktung-jakobs.jpg",
  },
  {
    id: "marketing",
    label: "Besichtigungen & Verhandlung",
    title: "Mit Fingerspitzengefühl verhandeln",
    icon: <IconNumber4  className="size-4 md:size-auto" />,
    content:
      "Mit modernen Marketingstrategien sorgen wir dafür, dass Ihre Immobilie von der richtigen Zielgruppe gesehen wird.",
    image: "/Fotos/verkaufen-steps-besichtigung-jakobs.jpg",
  },
  {
    id: "besichtigung",
    label: "Kaufvertrag & Notartermin",
    title: "Sicher zum Abschluss",
    icon: <IconNumber5  className="size-4 md:size-auto" />,
    content:
      "Persönliche Besichtigungen sind ein entscheidender Faktor für den Verkaufsprozess. Wir koordinieren und begleiten den gesamten Prozess.",
    image: "/Fotos/verkaufen-steps-notar-jakobs.jpg",
  },
  {
    id: "berichte",
    label: "Begleitung",
    title: "An Ihrer Seite auch nach dem Verkauf",
    icon: <IconNumber6  className="size-4 md:size-auto" />,
    content:
      "Transparenz ist uns wichtig. Wir bieten regelmäßige Berichte über den Fortschritt des Verkaufsprozesses.",
    image: "/Fotos/verkaufen-steps-begleitung-jakobs.jpg",
  },
];

export default function VerticalSteps() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  const [openAccordion, setOpenAccordion] = useState(tabs[0].id);

  // Preload images and icons when component mounts
  useEffect(() => {
    tabs.forEach((tab) => {
      const image = new Image();
      image.src = tab.image;
    });
  }, []);

  return (
    <div className="w-full max-w-[85rem] flex flex-col md:px-4 items-center gap-8 py-6">
      {/* Tabs Navigation (Only shown on desktop) */}
      <div className="hidden md:flex relative gap-6 border-b w-full max-w-[85rem] justify-between pb-3 overflow-x-auto no-scrollbar whitespace-nowrap">
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            variant="ghost"
            className={cn(
              "relative h-full flex flex-col items-center hover:bg-white font-normal leading-relaxed px-0 py-2 text-gray-400 hover:text-prime-900/80",
              activeTab === tab.id && "text-prime-950 font-medium"
            )}
            onClick={() => setActiveTab(tab.id)}
          >
            <div className="flex items-center justify-center w-6 h-6 mb-2 rounded-full bg-white border border-gray-300">
            {tab.icon}

            </div>
            <span className="text-base">{tab.label}</span>

            {/* Moving Indicator */}
            {activeTab === tab.id && (
              <motion.div
                layoutId="underline"
                className="absolute -bottom-3 w-32 h-[1px] bg-[#e95c0c] rounded-full"
                initial={false}
                transition={{ type: "easeInOut", stiffness: 100, damping: 20 }}
              />
            )}
          </Button>
        ))}
      </div>

      {/* Content Section (For desktop) */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.4 }}
          className="hidden md:flex flex-col md:flex-row items-center gap-6 text-center px-4"
        >
          <img
            src={tabs.find((tab) => tab.id === activeTab)?.image}
            alt={tabs.find((tab) => tab.id === activeTab)?.label}
            className="w-1/2 aspect-video object-cover h-auto shadow-md"
            width={600}       // real pixel dimensions
            height={400}
            style={{ objectFit: 'cover' }}
          />
          <div className="text-left">
            <h2 className="text-2xl font-serif font-semibold text-gray-800">
              {tabs.find((tab) => tab.id === activeTab)?.title}
            </h2>
            <p className="text-gray-600 text-lg mt-2">
              {tabs.find((tab) => tab.id === activeTab)?.content}
            </p>
            <BeratungsterminModal trigger={<ButtonPrime icon={<CalendarDateRangeIcon className="size-7"/>} className="mt-5" text="Beratungstermin vereinbaren" />} />
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Accordion for mobile screens */}
      <div className="w-full md:hidden">
        <div className="w-full max-w-4xl mx-auto -4 ">
          {tabs.map((tab, index) => {
            // Is this step currently open?
            const isOpen = openAccordion === tab.id;
            return (
              <div key={tab.id} className="mb-8 flex">
                {/* Left: Icon + Vertical Line */}
                <div className="flex flex-col items-center mr-4">
                  <div
                    className={`w-8 h-8 flex items-center justify-center border rounded-full ${
                      isOpen
                        ? "border-orange-400 shadow-md"
                        : "bg-white border-gray-300"
                    }`}
                  >
            {tab.icon}

                  </div>
                  {/* Vertical line between icons */}
                  {index < tabs.length - 1 && (
                    <div
                      className="w-[0.1px] flex-1 mt-2"
                      style={{
                        backgroundColor: isOpen ? "#f316" : "#d1d5db",
                      }}
                    />
                  )}
                </div>

                {/* Right: Tab Content */}
                <div className="flex-1">
                  <button
                    onClick={() => setOpenAccordion(tab.id)}
                    className="w-full text-left focus:outline-none"
                  >
                    <h2
                      className={`  transition-colors ${
                        isOpen
                          ? "text-sm font-normal text-orange-600"
                          : "text-base  text-text-100"
                      }`}
                    >
                      {tab.label}
                    </h2>
                  </button>
                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        key={tab.id}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="overflow-hidden"
                      >
                        <h2
                      className="text-lg my-2 font-serif transition-colors font-normal text-dark-100"
                    >
                      {tab.title}
                    </h2>
                        <img
                          src={tab.image}
                          alt={tab.label}
                          className="w-full h-auto mt-4"
                          width={600}       // real pixel dimensions
                          height={400}
                          style={{ objectFit: 'cover' }}
                        />
                        <p className="text-gray-600 text-base font-light mt-4">
                          {tab.content}
                        </p>
                        <BeratungsterminModal trigger={<ButtonPrime icon={<CalendarDateRangeIcon className="size-7"/>} className="my-5" text="Beratungstermin vereinbaren" />} />

                      </motion.div>
                    )}
                    <hr className="translate-y-5 " />
                  </AnimatePresence>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
